import { CircleAlert, Info, TriangleAlert, X } from "lucide-react";
import type { JSX, ReactNode } from "react";

import { t } from "@/i18n";
import { cn } from "@/lib/cn";

export type BannerTone = "info" | "warning" | "danger";

const TONE: Record<BannerTone, string> = {
  info: "border-info-soft bg-info-soft text-info-ink",
  warning: "border-warning-soft bg-warning-soft text-warning-ink",
  danger: "border-danger-soft bg-danger-soft text-danger-ink",
};

const ICON = { info: Info, warning: TriangleAlert, danger: CircleAlert };

/** A state that outlives the page: above the frame or a thread. Holatlar 5.1's three levels. */
export function Banner({
  tone,
  children,
  action,
  onDismiss,
  className,
}: {
  tone: BannerTone;
  children: ReactNode;
  /** The one way forward, and any "+2" beside it. */
  action?: ReactNode | undefined;
  onDismiss?: (() => void) | undefined;
  className?: string | undefined;
}): JSX.Element {
  const Icon = ICON[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn("flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2 text-sm", TONE[tone], className)}
    >
      <Icon className="size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <span className="min-w-0 flex-[1_1_200px]">{children}</span>
      {action}
      {onDismiss === undefined ? null : (
        <button type="button" aria-label={t("ui.close")} onClick={onDismiss} className="-mr-1 inline-flex size-7 items-center justify-center rounded-small hover:bg-surface/50">
          <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
